import { Button } from 'fluxo-ui';
import { EyeIcon, PlayIcon, TimesIcon } from 'fluxo-ui/icons';

interface ExamPreviewProps {
    exam: {
        title: string;
        description: string;
        type: string;
        instructions?: string;
        questions: any[];
        settings: any;
    };
    onClose?: () => void;
    onStart?: () => void;
}

const ExamPreview: React.FC<ExamPreviewProps> = ({ exam, onClose, onStart }) => {
    const formatDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const totalMarks = exam.questions.reduce((sum, q) => sum + (q.marks || 0), 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                    <EyeIcon className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                    <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">Exam Preview</h3>
                        <p className="text-xs text-slate-600 dark:text-slate-400">This is how students will see the exam overview</p>
                    </div>
                </div>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                    >
                        <TimesIcon className="w-5 h-5" />
                    </button>
                )}
            </div>

            {/* Exam Card */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-teal-500 to-purple-600 p-8 text-white">
                    <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-medium mb-4">
                        {(exam.type || '').replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <h1 className="text-3xl font-bold mb-2">{exam.title}</h1>
                    <p className="text-teal-100">{exam.description}</p>
                </div>

                {/* Exam Info Grid */}
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Exam Information</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">{exam.questions.length}</div>
                            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Questions</div>
                        </div>
                        <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{totalMarks}</div>
                            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Total Marks</div>
                        </div>
                        <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                {formatDuration(exam.settings?.duration || 3600)}
                            </div>
                            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Duration</div>
                        </div>
                        <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{exam.settings?.maxAttempts || 1}</div>
                            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Attempts</div>
                        </div>
                    </div>
                </div>

                {/* Instructions */}
                {exam.instructions && (
                    <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                        <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Instructions</h3>
                        <div className="prose dark:prose-invert max-w-none text-sm text-slate-600 dark:text-slate-400">
                            <div dangerouslySetInnerHTML={{ __html: exam.instructions }} />
                        </div>
                    </div>
                )}

                {/* Important Notes */}
                <div className="p-6 bg-teal-50 dark:bg-teal-900/20 border-b border-teal-200 dark:border-teal-800">
                    <h3 className="font-semibold text-teal-900 dark:text-teal-100 mb-3 flex items-center gap-2">
                        <span>⚠️</span>
                        <span>Important Notes</span>
                    </h3>
                    <ul className="space-y-2 text-sm text-teal-800 dark:text-teal-200">
                        {exam.settings?.fullscreenRequired && <li>• Fullscreen mode is required throughout the exam</li>}
                        {exam.settings?.trackWindowFocus && <li>• Tab switches and window changes will be monitored</li>}
                        {exam.settings?.disableCopyPaste && <li>• Copy and paste functions are disabled</li>}
                        {!exam.settings?.allowBackwardNavigation && <li>• You cannot go back to previous questions once answered</li>}
                        {exam.settings?.webcamProctoring && <li>• Webcam recording will be enabled (requires permission)</li>}
                        {exam.settings?.autoSaveIntervalSeconds && (
                            <li>• Your answers will be auto-saved every {exam.settings.autoSaveIntervalSeconds} seconds</li>
                        )}
                    </ul>
                </div>

                {/* Question Overview */}
                <div className="p-6">
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Question Overview</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {exam.questions.map((q, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <span className="w-8 h-8 flex items-center justify-center bg-teal-100 dark:bg-teal-900 text-teal-600 dark:text-teal-400 rounded-full text-sm font-semibold">
                                        {index + 1}
                                    </span>
                                    <div>
                                        <div className="text-sm font-medium text-slate-900 dark:text-white">
                                            {q.title || `Question ${index + 1}`}
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400">
                                            {(q.type || '').replace(/_/g, ' ').toUpperCase()}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-medium text-slate-900 dark:text-white">
                                        {q.marks} mark{q.marks !== 1 ? 's' : ''}
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                        {Math.floor((q.timeSeconds || 0) / 60)} min
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Start Button */}
                {onStart && (
                    <div className="p-6 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-slate-600 dark:text-slate-400">
                                Make sure you have read all instructions carefully
                            </div>
                            <Button label="Start Exam" leftIcon={PlayIcon} variant="primary" size="lg" onClick={onStart} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExamPreview;
